/**
 * THE LIGHTMAP, ON DISK.
 *
 * The lightmapper is a good renderer and a terrible loading screen. It spends
 * forty-seven seconds on an M4 firing about forty million shadow rays at a
 * building that has not changed since the last commit, and every player pays
 * that on every page load to arrive at the same answer. This file is how that
 * answer gets shipped instead of recomputed.
 *
 * WHAT IS STORED IS THE LIGHT, NOT THE GEOMETRY. That is the whole trick and it
 * is worth being explicit about, because the obvious version of this feature —
 * serialise the baked meshes — would be an eighty megabyte download. The bake
 * has two halves: `subdivide`, which is pure arithmetic and takes about a
 * second, and the raycast loop, which is everything else. Subdivision is also
 * deterministic: the same source geometry and the same light set produce the
 * same vertices in the same order, every time, on every machine. So the client
 * can still tessellate at load — it is cheap — and all it needs from disk is one
 * irradiance value per resulting vertex.
 *
 * That determinism is load-bearing, which is why every input to it is hashed
 * into a signature and checked. Move a lamp, retessellate a floor, change
 * `MAX_EDGE`, and the file on disk describes a building that no longer exists;
 * the vertex it thinks is under a downlight is now halfway up a wall. A stale
 * lightmap does not fail loudly on its own, it just paints the wrong picture, so
 * the signature is the only thing standing between a missed rebake and a mall
 * lit by the ghost of a previous layout. On mismatch the client bakes live and
 * says so in the console.
 *
 * FORMAT. Little-endian throughout, because every platform this runs on is.
 *
 *   magic      u32   'MXBK'
 *   version    u32   bump on any change to the format OR the kernel
 *   signature  u32   FNV-1a over source geometry, lights and kernel constants
 *   meshes     u32
 *   counts     u32 * meshes    baked vertices per mesh, in `collect` order
 *   pixels     u8  * 4 * total RGBE, one per baked vertex
 *
 * RGBE rather than three floats, for the same reason Radiance used it in 1985:
 * this is high dynamic range data — the AgX pipeline drives fixtures far past
 * one — so eight-bit-per-channel with a fixed scale would band the dark half of
 * the building, and float32 would triple the file for precision that a value
 * added to a diffuse albedo cannot show.
 *
 * The error a shared exponent gives you is best stated per VERTEX rather than
 * per channel: every channel lands within about 0.4% of that vertex's own
 * brightest channel, anywhere in the range. So a vertex under a vitrine strip
 * and a vertex three rooms away are equally well described — which is the
 * property that matters, and the one a fixed scale does not have. Read
 * per-channel the same bound loosens in proportion to how far that channel sits
 * below its neighbours, and that is fine: an error measured against a colour's
 * own peak is a hue shift too small to name, and it costs nothing anywhere in a
 * building where every fixture is some flavour of warm or sodium white.
 */

/** 'MXBK' little-endian. */
const MAGIC = 0x4b42584d;

/**
 * Bump this whenever the bake changes shape.
 *
 * The signature catches a changed WORLD. It cannot catch a changed KERNEL,
 * because the kernel is code and code is not in the hash — so `MAX_EDGE`,
 * `MAX_SUBDIVISIONS`, the falloff curve and the soft-shadow tap pattern all live
 * behind this number instead. Getting it wrong is the failure mode this whole
 * file exists to prevent, so when in doubt, bump it: the cost is one slow load
 * for whoever forgot to rerun the baker.
 */
export const BAKE_CACHE_VERSION = 2;

const HEADER_BYTES = 16;

export interface BakeCache {
  signature: number;
  /** Baked vertex count per mesh, in `collect` order. */
  counts: Uint32Array;
  /** Irradiance for one mesh, decoded on demand. */
  read(mesh: number): Float32Array;
}

/**
 * FNV-1a over a float buffer, read as bytes.
 *
 * Cheap, well-distributed for this, and — the part that matters — it hashes the
 * exact bits. Two builds whose floats differ in the last mantissa bit produce
 * different subdivisions, so a hash that rounded first would be worse than none:
 * it would say "same building" about a building that bakes differently.
 */
export function hashFloats(seed: number, data: Float32Array): number {
  const bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  let h = seed >>> 0;
  for (let i = 0; i < bytes.length; i++) {
    h ^= bytes[i];
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Same hash, for the handful of scalars that describe a light. */
export function hashNumbers(seed: number, values: readonly number[]): number {
  return hashFloats(seed, Float32Array.from(values));
}

function rgbe(r: number, g: number, b: number, out: Uint8Array, at: number): void {
  const peak = Math.max(r, g, b);
  if (!(peak > 1e-8)) {
    out[at] = out[at + 1] = out[at + 2] = out[at + 3] = 0;
    return;
  }
  // frexp, by hand: the smallest exponent e with peak < 2^e. The +128 bias is
  // what lets one unsigned byte carry the range either side of 1.0.
  let e = Math.ceil(Math.log2(peak));
  // log2 of an exact power of two lands on the boundary. One correction step is
  // cheaper than reasoning about which side of it the library rounds to.
  if (peak / Math.pow(2, e) >= 1) e++;
  const scale = 255 / Math.pow(2, e);
  // ROUND, not truncate. Truncating looks like the same thing and is not: it
  // doubles the worst-case error and, worse, it is a one-sided error, so every
  // vertex in the building comes back a fraction darker than it was baked and
  // the whole mall sits under the exposure it was graded at.
  out[at] = Math.min(255, Math.round(r * scale));
  out[at + 1] = Math.min(255, Math.round(g * scale));
  out[at + 2] = Math.min(255, Math.round(b * scale));
  out[at + 3] = Math.max(0, Math.min(255, e + 128));
}

/** Pack per-mesh irradiance into one transferable buffer. */
export function encodeBakeCache(signature: number, values: Float32Array[]): ArrayBuffer {
  let total = 0;
  for (const v of values) total += v.length / 3;

  const buf = new ArrayBuffer(HEADER_BYTES + values.length * 4 + total * 4);
  const head = new DataView(buf);
  head.setUint32(0, MAGIC, true);
  head.setUint32(4, BAKE_CACHE_VERSION, true);
  head.setUint32(8, signature, true);
  head.setUint32(12, values.length, true);

  const counts = new Uint32Array(buf, HEADER_BYTES, values.length);
  const pixels = new Uint8Array(buf, HEADER_BYTES + values.length * 4);

  let at = 0;
  for (let m = 0; m < values.length; m++) {
    const v = values[m];
    const n = v.length / 3;
    counts[m] = n;
    for (let i = 0; i < n; i++) rgbe(v[i * 3], v[i * 3 + 1], v[i * 3 + 2], pixels, (at + i) * 4);
    at += n;
  }
  return buf;
}

/**
 * Read a cache, or null if it is not one, is from another version, or describes
 * a different building. Never throws: a bad file is a slow load, not a crash.
 */
export function decodeBakeCache(buf: ArrayBuffer, signature: number): BakeCache | null {
  if (buf.byteLength < HEADER_BYTES) return null;
  const head = new DataView(buf);
  if (head.getUint32(0, true) !== MAGIC) return null;
  if (head.getUint32(4, true) !== BAKE_CACHE_VERSION) return null;
  const stored = head.getUint32(8, true);
  if (stored !== signature) return null;

  const meshes = head.getUint32(12, true);
  if (buf.byteLength < HEADER_BYTES + meshes * 4) return null;
  const counts = new Uint32Array(buf.slice(HEADER_BYTES, HEADER_BYTES + meshes * 4));

  let total = 0;
  for (const c of counts) total += c;
  const base = HEADER_BYTES + meshes * 4;
  if (buf.byteLength < base + total * 4) return null;
  const pixels = new Uint8Array(buf, base);

  const offsets = new Uint32Array(meshes);
  let at = 0;
  for (let m = 0; m < meshes; m++) {
    offsets[m] = at;
    at += counts[m];
  }

  return {
    signature: stored,
    counts,
    read(mesh: number): Float32Array {
      const n = counts[mesh];
      const out = new Float32Array(n * 3);
      const from = offsets[mesh] * 4;
      for (let i = 0; i < n; i++) {
        const e = pixels[from + i * 4 + 3];
        if (!e) continue;
        const scale = Math.pow(2, e - 128) / 255;
        out[i * 3] = pixels[from + i * 4] * scale;
        out[i * 3 + 1] = pixels[from + i * 4 + 1] * scale;
        out[i * 3 + 2] = pixels[from + i * 4 + 2] * scale;
      }
      return out;
    },
  };
}
